<!-- eslint-disable vue/no-multiple-template-root -->
<template>
  <td class="account-table-item__account-icon-cell">
    <template v-if="shouldDisplayEditedIcon">
      <i
        v-tooltip="editedTooltip"
        class="
          account-table-item__account-icon-cell__icon
          icon-edit
          account-table-item__account-icon-cell__icon--moved
        "
        data-testid="account-table-item__account-icon-cell__icon"
      />
    </template>
    <template v-else>
      <externally-mapped-icon v-if="isMappedToExternalNumber" />
      <template v-else>
        <i
          v-if="hasProposals && someAccountsHaveExternalMapping"
          v-tooltip="proposalTooltip"
          class="icon-magic"
        />
      </template>
    </template>
    <i
      v-if="status === accountTableRowStatuses.UNCERTAIN"
      v-tooltip="lowConfidenceTooltip"
      data-testid="low-confidence-icon"
      class="
        icon-exclamation-circle
        account-table-item__account-icon-cell__icon--low-confidence
      "
    />
  </td>

  <td
    class="account-table-item__account-mapped-name sf-hover"
    data-testid="account-table-item-mapped-name"
  >
    <div class="sf-level sf-level-between">
      <div
        v-if="mappedAccountNumber"
        data-testid="account-table-item-mapped-identifier"
      >
        {{ mappedAccountNumber }} {{ mappedAccountDisplayName }}
      </div>
      <div
        v-else
        class="sf-dim-el"
        data-testid="account-table-item-mapped-identifier"
      >
        {{ noAiProposalText }}
      </div>
      <div :class="remapClasses">
        <remap-dropdown
          :account-id="itemId"
          :account-range="item.mappedAccount?.accountRange || '0'"
          :items="mappingProposals"
          :externally-mapped-number="displayExternallyMappedAccounts"
        />
      </div>
    </div>
    <div
      v-if="item.isLowConfidence && !item.mappedAccount?.isSaved"
      class="sf-pad-2 sf-bg-neutral-subtle-2 sf-layer-rounded"
    >
      <remap-inline-item
        v-for="accountRange in inlineMappingProposals"
        :key="accountRange"
        :account-range="accountRange"
        :current-account-range="mappedAccountNumber"
        @item-clicked="handleInlineProposalClicked"
      />
    </div>
  </td>

  <td data-testid="account-table-item-original-identifier">
    {{ item.number }} {{ displayName }}
  </td>

  <td class="sf-align-r">
    {{ item.balance?.formattedValue }}
  </td>

  <td>
    <div class="sf-level sf-level-between">
      <div>{{ translatedAccountType(item.mappedAccountType) }}</div>
      <div v-if="accountTypeMismatch">
        <i
          v-tooltip.html="accountTypeMismatchTooltip"
          data-testid="account-type-mismatch-icon"
          class="
            icon icon-exclamation-triangle
            account-table-item__account-type-cell-icon
          "
        />
      </div>
    </div>
  </td>
</template>

<script setup lang="ts">
import RemapDropdown from './remap-dropdown.vue'
import RemapInlineItem from './remap-inline-item.vue'
import {
  pickAccountNameByLocale,
  pickGroupLeafNameByFirstAvailableLocale,
} from '../utils'
import { useGroupList } from '../store/group-list'
import { useLocale } from '../store/locale'
import { AccountId, AccountRange, AccountTableRow } from '../shared-types'
import { accountTableRowStatuses } from '../constants'
import { computed } from 'vue'
import ExternallyMappedIcon from './externally-mapped-icon.vue'
import { useCommon } from '../store/common'
import { storeToRefs } from 'pinia'

interface Props {
  item: AccountTableRow
  itemId: AccountId
  focused: boolean
}
const props = withDefaults(defineProps<Props>(), {
  focused: () => false,
})

const groupListStore = useGroupList()
const localeStore = useLocale()

const { accounts } = storeToRefs(useCommon())

const someAccountsHaveExternalMapping = computed(() => {
  return Object.keys(accounts.value).some((accountId) => {
    return accounts.value[accountId].externallyMappedNumber
  })
})

const noAiProposalText = window.I18n.t('account_mapping_list.no_ai_proposal')

const mappedAccountNumber = computed(() => props.item.mappedAccountNumber)
const mappingProposals = computed(() => props.item.recommendation.proposals)
const hasProposals = computed(() => mappingProposals.value.length > 0)

const shouldDisplayEditedIcon = computed(
  () => editedAIProposal.value || editedExternallyProposal.value
)

const editedAIProposal = computed(
  () =>
    mappingProposals.value.length > 0 &&
    mappedAccountNumber.value &&
    mappedAccountNumber.value !== props.item.initialMappedNumber &&
    status.value === accountTableRowStatuses.EDITED
)

const editedExternallyProposal = computed(
  () =>
    hasExternallyMappedNumber.value &&
    mappedAccountNumber.value &&
    mappedAccountNumber.value !== displayExternallyMappedAccounts.value &&
    status.value === accountTableRowStatuses.EDITED
)

const displayExternallyMappedAccounts = computed(
  () => props.item.externallyMappedNumber
)

const hasExternallyMappedNumber = computed(
  () => props.item.externallyMappedNumber !== null
)

const isMappedToExternalNumber = computed(
  () => props.item.externallyMappedNumber === mappedAccountNumber.value
)

const inlineMappingProposals = computed(() =>
  mappingProposals.value.slice(0, 3)
)

const status = computed(() => props.item.status)

const proposalTooltip = computed(() => {
  return window.I18n.t('account_mapping_list.tooltip.proposal_tooltip')
})

const lowConfidenceTooltip = computed(() => {
  return window.I18n.t('account_mapping_list.tooltip.low_confidence_tooltip')
})

const editedTooltip = computed(() => {
  if (editedAIProposal.value || editedExternallyProposal.value) {
    return window.I18n.t('account_mapping_list.tooltip.edited_proposal')
  } else {
    return ''
  }
})

const displayName = computed(() => {
  return pickAccountNameByLocale({
    account: props.item,
    noNamePrefix: false,
    supportedLocales: localeStore.supportedLocales,
  })
})

const mappedAccountDisplayName = computed(() => {
  return pickGroupLeafNameByFirstAvailableLocale(
    props.item.mappedAccount,
    localeStore.supportedLocales
  )
})

const accountTypeMismatch = computed(() => {
  return (
    mappedAccountNumber.value &&
    props.item.accountTypeExistsInMappingList &&
    props.item.accountTypeExplicitlySet &&
    props.item.mappedAccount.type !== props.item.accountType
  )
})

const remapClasses = computed(() => ({ 'sf-hover-item': !props.focused }))

const accountTypeMismatchTooltip = computed(() => {
  return window.I18n.t('account_mapping_list.tooltip.account_type_mismatch', {
    account_type: translatedAccountType(props.item.accountType),
    mapped_account_type: translatedAccountType(props.item.mappedAccount.type),
  })
})

const translatedAccountType = (type: string | undefined) =>
  type ? window.I18n.t(`account.account_types.${type}`) : ''

const handleInlineProposalClicked = function (newAccountRange: AccountRange) {
  groupListStore.remap({
    accountId: props.itemId,
    prevAccountRange: mappedAccountNumber.value,
    accountRange: newAccountRange,
  })
}
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.account-table-item {
  &__account-icon-cell {
    border-right: 0;
    padding-right: 0;

    &__container {
      display: inline-block;
      width: 100%;
      vertical-align: middle;
      text-align: center;
    }

    &__icon {
      $size: 15px;

      line-height: $size;
      width: $size;
      font-size: $size;
      text-align: center;
      position: relative;
      top: 2px;

      &--low-confidence {
        @include color('danger');
      }

      &--moved {
        @include color('gray', 'dark');
      }
    }
  }

  &__account-mapped-name {
    border-left: 0;
  }

  &__account-type-cell-icon {
    @include color('warning');
  }
}
</style>
